import { Pencil, Plus } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import type { Skill } from "@/api/types";
import { useAuth } from "@/auth/useAuth";
import { EmptyState } from "@/components/feedback/EmptyState";
import { ErrorState } from "@/components/feedback/ErrorState";
import { LoadingState } from "@/components/feedback/LoadingState";
import { PageHeader } from "@/components/layout/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DataTable, type DataTableColumn } from "@/components/ui/data-table";
import { ViewToggle, useViewMode } from "@/components/ui/view-toggle";
import { SkillCard } from "@/features/skills/components/SkillCard";
import { SkillFormDialog } from "@/features/skills/components/SkillFormDialog";
import { useSkills } from "@/features/skills/hooks/useSkills";
import { useTeams } from "@/features/teams/hooks/useTeams";

export function SkillListPage() {
  const { t } = useTranslation("common");
  const [showInactive, setShowInactive] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Skill | null>(null);
  const [view, setView] = useViewMode("skills:view", "cards");

  const { data, isLoading, isError, error, refetch } = useSkills({
    include_inactive: showInactive,
  });
  const { data: teams } = useTeams({ include_inactive: true });
  const { isAdmin, memberships } = useAuth();
  const canCreateSomewhere =
    isAdmin ||
    memberships.some((m) => m.role === "manager" || m.role === "creator");

  const teamMap = useMemo(
    () => new Map(teams?.map((tm) => [tm.id, tm.name]) ?? []),
    [teams],
  );

  const columns = useMemo<DataTableColumn<Skill>[]>(
    () => [
      {
        key: "name",
        header: t("pages.skills.columns.name"),
        render: (s) => <span className="font-medium text-ink-950">{s.name}</span>,
      },
      {
        key: "description",
        header: t("pages.skills.columns.description"),
        className: "max-w-md truncate text-ink-500",
        render: (s) => s.description,
      },
      {
        key: "team",
        header: t("pages.skills.columns.team"),
        render: (s) => teamMap.get(s.owner_team_id) ?? "—",
      },
      {
        key: "active",
        header: t("pages.skills.columns.active"),
        render: (s) => (
          <Badge variant={s.is_active ? "success" : "destructive"}>
            {s.is_active ? t("pages.skills.columns.active") : t("pages.teams.inactive")}
          </Badge>
        ),
      },
      {
        key: "actions",
        header: "",
        render: (s) =>
          canCreateSomewhere ? (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setEditing(s);
                setFormOpen(true);
              }}
            >
              <Pencil className="h-3 w-3" />
              {t("actions.edit")}
            </Button>
          ) : null,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, teamMap, canCreateSomewhere],
  );

  if (isLoading) return <LoadingState />;
  if (isError)
    return <ErrorState error={error} onRetry={() => void refetch()} />;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={t("pages.skills.title")}
        description={t("pages.skills.description")}
        actions={
          <>
            <Button
              variant={showInactive ? "secondary" : "outline"}
              onClick={() => setShowInactive((v) => !v)}
            >
              {showInactive
                ? t("pages.llmModels.hideInactive")
                : t("pages.llmModels.showInactive")}
            </Button>
            <ViewToggle
              value={view}
              onChange={setView}
              labels={{
                cards: t("pages.skills.viewCards"),
                list: t("pages.skills.viewList"),
              }}
            />
            <Button
              onClick={() => {
                setEditing(null);
                setFormOpen(true);
              }}
              disabled={!canCreateSomewhere}
            >
              <Plus className="h-4 w-4" />
              {t("pages.skills.new")}
            </Button>
          </>
        }
      />

      {data && data.length === 0 ? (
        <EmptyState
          title={t("pages.skills.empty")}
          description={t("pages.skills.emptyHint")}
        />
      ) : view === "list" ? (
        <DataTable
          columns={columns}
          rows={data ?? []}
          getRowKey={(s) => s.id}
        />
      ) : (
        <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {data?.map((skill) => (
            <SkillCard
              key={skill.id}
              skill={skill}
              onEdit={() => {
                setEditing(skill);
                setFormOpen(true);
              }}
            />
          ))}
        </ul>
      )}

      <SkillFormDialog
        open={formOpen}
        onOpenChange={(v) => {
          setFormOpen(v);
          if (!v) setEditing(null);
        }}
        skill={editing}
      />
    </div>
  );
}
